import { useState } from "react";
import { TrashIcon } from "vega-icons";

import { Spinner } from "@vega-core/components/Loaders/LoadingSpinner/Spinner";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { useToast } from "@vega-core/components/Toast/useToast";
import { type UseBooleanReturn } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { useSavedFilters } from "@vega-modules/hooks/useSavedFilters";

import { type TableFilterId } from "../../types";

export const ManageFiltersModal = ({
  manageModal,
  id,
  applyContextInPersistenceKeys = true,
}: {
  manageModal: UseBooleanReturn;
  id: TableFilterId;
  applyContextInPersistenceKeys?: boolean;
}) => {
  const { savedFilters, deleteFilter, isDeleting } = useSavedFilters(
    id,
    applyContextInPersistenceKeys,
  );
  const showToast = useToast();

  const [deletingFilterId, setDeletingFilterId] = useState<string | null>(null);

  const handleDelete = async (filterId?: string) => {
    if (!filterId) return;

    setDeletingFilterId(filterId);

    try {
      await deleteFilter(filterId);
      setDeletingFilterId(null);
    } catch (error) {
      console.error(error);
      showToast({
        title: m.common_error_somethingWentWrong(),
        variant: "destructive",
      });
      setDeletingFilterId(null);
    }
  };

  return (
    <ActionaryModal
      defaultWidth
      open={manageModal.isOpen}
      setOpen={manageModal.setIsOpen}
      title={m.common_manage_filters()}
    >
      <div className="flex flex-col divide-y divide-border/primary">
        {savedFilters.map((filter) => (
          <div
            className={cnTwMerge(
              "flex items-center justify-between body-base-medium py-5",
            )}
            key={filter.id}
          >
            <div>{filter.filterName}</div>
            {deletingFilterId === filter.id ? (
              <Spinner asIcon />
            ) : (
              <TrashIcon
                className="text-text/secondary hover:text-text/negative defaultTransition cursor-pointer"
                onClick={() => !isDeleting && handleDelete(filter.id)}
              />
            )}
          </div>
        ))}
      </div>
    </ActionaryModal>
  );
};
